import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useUserProfile } from '../../hooks/useUserProfile'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { cn } from '../../lib/utils'
import { logError } from '../../utils/logger'
import { navigateWithPageTransition } from '../../utils/pageTransitions'
import { MobileAuthenticatedUtilitySheet } from './MobileAuthenticatedUtilitySheet'

export interface MobileAuthenticatedTopBarProps {
  className?: string
}

export const MOBILE_AUTHENTICATED_TOP_BAR_HEIGHT_CLASS = 'h-16'
export const MOBILE_AUTHENTICATED_TOP_BAR_SURFACE_CLASS =
  'bg-background-50/95 backdrop-blur-[12px]'
export const MOBILE_AUTHENTICATED_TOP_BAR_INNER_CLASS =
  'mx-auto flex h-full w-full max-w-md items-center justify-between px-5'

export const MobileAuthenticatedTopBar: React.FC<
  MobileAuthenticatedTopBarProps
> = ({ className }) => {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { profile } = useUserProfile()
  const [menuOpen, setMenuOpen] = React.useState(false)

  const resolvedName =
    profile?.display_name ||
    user?.user_metadata?.display_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'User'
  const userInitial = resolvedName.charAt(0).toUpperCase()

  const handleDestinationClick = (path: string) => {
    setMenuOpen(false)
    navigateWithPageTransition(navigate, path)
  }

  const handleSignOutClick = async () => {
    try {
      setMenuOpen(false)
      await signOut()
      navigate('/login')
    } catch (error) {
      logError('MobileAuthenticatedTopBar', 'Failed to sign out', error)
    }
  }

  return (
    <>
      <header
        className={cn(
          `sticky top-0 z-30 ${MOBILE_AUTHENTICATED_TOP_BAR_HEIGHT_CLASS} ${MOBILE_AUTHENTICATED_TOP_BAR_SURFACE_CLASS}`,
          className
        )}
      >
        <div className={MOBILE_AUTHENTICATED_TOP_BAR_INNER_CLASS}>
          <Link
            to="/boards"
            aria-label="Back to boards"
            className="inline-flex items-center text-ink-700 no-underline transition-colors duration-settle hover:text-primary-500"
          >
            <img
              src="/favicon-brown.svg"
              width={20}
              height={20}
              alt=""
              aria-hidden="true"
            />
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open account menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 transition-transform duration-settle ease-precise active:scale-[0.98] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#F4EFE2,0_0_0_4px_rgba(61,59,54,0.24)]"
          >
            <Avatar className="h-8 w-8">
              {profile?.avatar_url ? (
                <AvatarImage src={profile.avatar_url} alt={resolvedName} />
              ) : null}
              <AvatarFallback className="bg-primary-400 text-background-50 text-xs font-medium">
                {userInitial}
              </AvatarFallback>
            </Avatar>
          </button>
        </div>
      </header>

      <MobileAuthenticatedUtilitySheet
        open={menuOpen}
        onOpenChange={setMenuOpen}
        resolvedName={resolvedName}
        userInitial={userInitial}
        avatarUrl={profile?.avatar_url}
        email={user?.email}
        onDestinationClick={handleDestinationClick}
        onSignOutClick={handleSignOutClick}
      />
    </>
  )
}

export default MobileAuthenticatedTopBar
